/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useEffect, useState, useMemo } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate } from 'react-router-dom';
import { Component, Participant, ParticipantType } from '../../types';
import apiClient from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ParticipantModal from '../common/ParticipantModal';
import './PdfList.css';

type SortField = 'id' | 'name' | 'manufacturerName' | 'manufacturerCode' | 'uploadDate' | 'expirationDate' | 'price';
type SortDirection = 'asc' | 'desc';

const PdfList: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [searchFilters, setSearchFilters] = useState({
    expirationDate: '',
    lastUsedInBidDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    fetchComponents();
  }, []);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getAllComponents();
      if (response.success && response.components) {
        setComponents(response.components);
      } else {
        setError(response.error || 'Failed to load components');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading components');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (price?: number) => {
    if (price === null || price === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const closeParticipantModal = () => {
    setSelectedParticipant(null);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const handleFilterChange = (field: string, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearFilters = () => {
    setSearchFilters({
      expirationDate: '',
      lastUsedInBidDate: ''
    });
  };

  // Filter and sort components
  const filteredAndSortedComponents = useMemo(() => {
    // First filter
    const filtered = components.filter(component => {
      // Expiration date filter
      if (searchFilters.expirationDate && component.expirationDate) {
        if (!component.expirationDate.includes(searchFilters.expirationDate)) {
          return false;
        }
      }

      // Last used in bid date filter
      if (searchFilters.lastUsedInBidDate && component.lastUsedInBidDate) {
        if (!component.lastUsedInBidDate.includes(searchFilters.lastUsedInBidDate)) {
          return false;
        }
      }

      return true;
    });

    // Then sort
    return [...filtered].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'id':
          aVal = a.id;
          bVal = b.id;
          break;
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'manufacturerName':
          aVal = (a.manufacturerName || '').toLowerCase();
          bVal = (b.manufacturerName || '').toLowerCase();
          break;
        case 'manufacturerCode':
          aVal = (a.manufacturerCode || '').toLowerCase();
          bVal = (b.manufacturerCode || '').toLowerCase();
          break;
        case 'uploadDate':
          aVal = a.uploadDate || '';
          bVal = b.uploadDate || '';
          break;
        case 'expirationDate':
          aVal = a.expirationDate || '';
          bVal = b.expirationDate || '';
          break;
        case 'price':
          aVal = a.price ?? -Infinity;
          bVal = b.price ?? -Infinity;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [components, searchFilters, sortField, sortDirection]);

  // Check if any filters are active
  const hasActiveFilters = Object.values(searchFilters).some(value => value !== '');

  if (loading) {
    return (
      <div className="pdf-list-container">
      <BackButton to="/" />
        <div className="loading">Loading components...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-list-container">
        <BackButton to="/" />
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchComponents} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-list-container">
      <BackButton to="/" />
      <div className="pdf-list-header">
        <div className="header-left">
          <h1>Components</h1>
          <div className="pdf-count">
            {hasActiveFilters ? (
              <>Showing {filteredAndSortedComponents.length} of {components.length}</>
            ) : (
              <>{components.length} {components.length === 1 ? 'Component' : 'Components'}</>
            )}
          </div>
        </div>
        <div className="header-actions">
          <button
            className="toggle-filters-button"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          {(currentUser?.type === ParticipantType.MANAGER || currentUser?.type === ParticipantType.ADMIN) && (
            <button
              className="create-pdf-button"
              onClick={() => navigate('/components/create')}
            >
              Create New Component
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="search-filters">
          <div className="filter-header">
            <h3>Search & Filter</h3>
            {hasActiveFilters && (
              <button className="clear-filters-button" onClick={handleClearFilters}>
                Clear All Filters
              </button>
            )}
          </div>
          <div className="filter-grid">
            <div className="filter-field">
              <label htmlFor="filter-expiration-date">Expiration Date</label>
              <input
                id="filter-expiration-date"
                type="date"
                value={searchFilters.expirationDate}
                onChange={(e) => handleFilterChange('expirationDate', e.target.value)}
              />
            </div>

            <div className="filter-field">
              <label htmlFor="filter-last-used">Last Used in Bid</label>
              <input
                id="filter-last-used"
                type="date"
                value={searchFilters.lastUsedInBidDate}
                onChange={(e) => handleFilterChange('lastUsedInBidDate', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {components.length === 0 ? (
        <div className="empty-state">
          <p>No components found</p>
        </div>
      ) : (
        <div className="pdf-table-container">
          <table className="pdf-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                  ID{getSortIndicator('id')}
                </th>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  Name{getSortIndicator('name')}
                </th>
                <th onClick={() => handleSort('manufacturerName')} style={{ cursor: 'pointer' }}>
                  Manufacturer{getSortIndicator('manufacturerName')}
                </th>
                <th onClick={() => handleSort('manufacturerCode')} style={{ cursor: 'pointer' }}>
                  Manufacturer Code{getSortIndicator('manufacturerCode')}
                </th>
                <th onClick={() => handleSort('uploadDate')} style={{ cursor: 'pointer' }}>
                  Upload Date{getSortIndicator('uploadDate')}
                </th>
                <th onClick={() => handleSort('expirationDate')} style={{ cursor: 'pointer' }}>
                  Expiration Date{getSortIndicator('expirationDate')}
                </th>
                <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                  Price{getSortIndicator('price')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedComponents.map((component) => (
                <tr
                  key={component.id}
                  onClick={() => navigate(`/components/${component.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{component.id}</td>
                  <td className="pdf-name">{component.name}</td>
                  <td>{component.manufacturerName || 'N/A'}</td>
                  <td>{component.manufacturerCode || 'N/A'}</td>
                  <td>{formatDate(component.uploadDate)}</td>
                  <td>{formatDate(component.expirationDate)}</td>
                  <td className="price-cell">{formatPrice(component.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedParticipant && (
        <ParticipantModal participant={selectedParticipant} onClose={closeParticipantModal} />
      )}
    </div>
  );
};

export default PdfList;
